import { type ReactNode, useEffect } from 'react';
import { getTelegramWebApp } from '@/platform/telegram';

export const THEME_MODE_KEY = 'skvoz:theme-mode';
export type ThemeMode = 'system' | 'light' | 'dark';

export function applyTheme(theme: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', theme);
}

export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    const tg = getTelegramWebApp();
    if (tg) return tg.colorScheme === 'light' ? 'light' : 'dark';
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  }
  return mode;
}

function readSavedMode(): ThemeMode {
  const v = localStorage.getItem(THEME_MODE_KEY);
  if (v === 'light' || v === 'dark' || v === 'system') return v;
  return 'system';
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const mode = readSavedMode();
    applyTheme(resolveTheme(mode));

    if (mode === 'system') {
      const tg = getTelegramWebApp();
      if (tg) {
        const onChanged = () => applyTheme(resolveTheme('system'));
        tg.onEvent('themeChanged', onChanged);
        return () => tg.offEvent('themeChanged', onChanged);
      }
      const mq = window.matchMedia('(prefers-color-scheme: light)');
      const onMqChange = () => applyTheme(resolveTheme('system'));
      mq.addEventListener('change', onMqChange);
      return () => mq.removeEventListener('change', onMqChange);
    }
    return undefined;
  }, []);

  return <>{children}</>;
}
